import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useManageAdminOvertimes from './useManageAdminOvertimes';
import AdminService from '../../setting/admin/AdminService';
import AdminOvertimeService from './AdminOvertimeService';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const AdminOvertime = () => {
  const [mode, setMode] = useState('register');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [overtime, setOvertime] = useState({
    id: '',
    adminId: '',
    date: new Date(),
    hours: '',
    notes: ''
  });
  const [admins, setAdmins] = useState([]);
  
  // Filter states
  const [filterAdminId, setFilterAdminId] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const {
    overtimes,
    isLoading,
    error,
    addOvertime,
    editOvertime,
    deleteOvertime,
    refreshOvertimes
  } = useManageAdminOvertimes();

  // Load admins
  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const adminsRes = await AdminService.getAdmins();
        setAdmins(adminsRes.data);
      } catch (err) {
        console.error('Error loading admins:', err);
      }
    };
    loadAdmins();
  }, []);

  // Load overtime records with filters
  useEffect(() => {
    const params = {};
    if (filterAdminId) params.adminId = filterAdminId;
    if (filterMonth) params.month = filterMonth;
    if (filterYear) params.year = filterYear;
    refreshOvertimes(params);
    setCurrentPage(1);
  }, [filterAdminId, filterMonth, filterYear]);

  // Filter and paginate records
  const filteredOvertimes = overtimes.filter(ot => {
    if (filterMonth && filterYear) {
      const otDate = new Date(ot.date);
      const otMonth = otDate.getMonth() + 1;
      const otYear = otDate.getFullYear();
      if (otMonth !== parseInt(filterMonth) || otYear !== parseInt(filterYear)) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredOvertimes.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOvertimes = filteredOvertimes.slice(indexOfFirstItem, indexOfLastItem);

  // Generate year options (starting from 2026)
  const currentYear = new Date().getFullYear();
  const startYear = 2026;
  const yearOptions = [];
  for (let y = currentYear; y >= startYear; y--) {
    yearOptions.push(y);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOvertime(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date) => {
    setOvertime(prev => ({
      ...prev,
      date: date
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!overtime.adminId) {
      toast.error('Please select an admin');
      return;
    }
    if (!overtime.date) {
      toast.error('Please select a date');
      return;
    }
    if (!overtime.hours || parseFloat(overtime.hours) <= 0) {
      toast.error('Please enter valid hours (greater than 0)');
      return;
    }

    try {
      const overtimeData = {
        adminId: parseInt(overtime.adminId),
        date: overtime.date.toISOString().split('T')[0],
        hours: parseFloat(overtime.hours),
        notes: overtime.notes || null
      };

      if (mode === 'register') {
        await addOvertime(overtimeData);
      } else {
        await editOvertime(overtime.id, overtimeData);
      }

      handleCloseForm();
    } catch (err) {
      console.error('Error saving overtime:', err);
    }
  };

  const handleEdit = (ot) => {
    setMode('edit');
    setOvertime({
      id: ot.id,
      adminId: ot.adminId.toString(),
      date: new Date(ot.date),
      hours: ot.hours.toString(),
      notes: ot.notes || ''
    });
  };

  const handleDelete = (id) => {
    setOvertime(prev => ({ ...prev, id }));
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteOvertime(overtime.id);
      setIsConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting overtime:', err);
    }
  };

  const handleCloseForm = () => {
    setMode('register');
    setOvertime({
      id: '',
      adminId: '',
      date: new Date(),
      hours: '',
      notes: ''
    });
  };

  const handleAddNew = () => {
    handleCloseForm();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4">
        <h2 className='text-5xl mb-4'>Record Overtime</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: List of Records */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Overtime Records</h3>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 border-b border-gray-200 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Admin
                  </label>
                  <select
                    value={filterAdminId}
                    onChange={(e) => setFilterAdminId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Admins</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>
                        {admin.nickname || `Admin #${admin.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Month
                  </label>
                  <select
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Months</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                      <option key={month} value={month}>
                        {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Year
                  </label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Years</option>
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Records Table */}
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      No
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : currentOvertimes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-gray-500">
                        No overtime records found
                      </td>
                    </tr>
                  ) : (
                    currentOvertimes.map((ot, index) => {
                      return (
                        <tr key={ot.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-900">
                            {indexOfFirstItem + index + 1}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                            {ot.admin?.nickname || `Admin #${ot.adminId}`}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(ot.date).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                            {parseFloat(ot.hours).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleEdit(ot)}
                              className="text-indigo-600 hover:text-indigo-900 mr-3"
                            >
                              <PencilIcon className="h-5 w-5 inline" />
                            </button>
                            <button
                              onClick={() => handleDelete(ot.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-5 w-5 inline" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(indexOfLastItem, filteredOvertimes.length)}</span> of{' '}
                      <span className="font-medium">{filteredOvertimes.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {[...Array(totalPages)].map((_, idx) => {
                        const page = idx + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 3 || page === currentPage + 3) {
                          return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                        }
                        return null;
                      })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Input Form */}
          <div className="bg-white rounded-lg shadow">
            <div className={`px-6 py-4 border-b-2 ${
              mode === 'edit' ? 'border-yellow-500 bg-yellow-50' : 'border-green-500 bg-green-50'
            }`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${
                  mode === 'edit' ? 'text-yellow-800' : 'text-green-800'
                }`}>
                  {mode === 'edit' ? '✏️ Edit Overtime Record' : '➕ Create New Overtime Record'}
                </h3>
                {mode === 'edit' && (
                  <button
                    onClick={handleAddNew}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    + Add New
                  </button>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin *
                </label>
                <select
                  name="adminId"
                  value={overtime.adminId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select Admin</option>
                  {admins.map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.nickname || `Admin #${admin.id}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <DatePicker
                  selected={overtime.date}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hours *
                </label>
                <input
                  type="number"
                  name="hours"
                  value={overtime.hours}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., 2.5"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  OT rate will be calculated from salary automatically
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  name="notes"
                  value={overtime.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {mode === 'edit' ? 'Update' : 'Create'} Overtime
                </button>
                {mode === 'edit' && (
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={() => setIsConfirmOpen(false)}
        style={customStyles}
        contentLabel="Delete Confirmation"
      >
        <DeleteConfirm
          confirmFunction={confirmDelete}
          closeConfirm={() => setIsConfirmOpen(false)}
          titleText="Delete Overtime Record"
        />
      </Modal>
    </div>
  );
};

export default AdminOvertime;

